import React, { useEffect, useState } from "react";
import StatTile from "../../apps/ClassroomApp/components/StatTile.jsx";
import { getCrmReportSummary } from "../../../services/CrmReportService";
import CrmListModal from "./CrmListModal.jsx";
import CrmCharts from "./CrmCharts.jsx";
import FilterSheet from "../common/FilterSheet/FilterSheet";
import CrmFilterFields, { NO_FILTERS, FILTER_FIELDS } from "./CrmFilterFields.jsx";
import "../Trainer/TrainerDashboard.css";
import "../common/lms-pages.css";

/* One CRM report -- the body of a CRM page (CrmReports.jsx): its figures
   as the kit's report tiles (.br-stats), a line under them answering the
   report's question, then its charts; a tile opens the list behind its number in a popup
   (CrmListModal). The figures, bands and the highlight all arrive from
   GET /crm-reports/:kind; this only draws them. */

// The API's tones, as StatTile's variants.
const VARIANT = { good: "free", bad: "crit", warn: "warn" };

const highlightLine = (kind, h) => {
  if (!h) return null;
  if (kind === "students") {
    return h.averageRate == null ? null : `Average attendance across ${h.enrolments} enrolments: ${h.averageRate}%.`;
  }
  const parts = [];
  if (h.topAttendance) parts.push(`Best attendance: ${h.topAttendance.batchName} (${h.topAttendance.attendanceRate}%)`);
  if (h.topFeedback) parts.push(`Best feedback: ${h.topFeedback.batchName} (${h.topFeedback.averageRating.toFixed(1)} ★ from ${h.topFeedback.responses})`);
  return parts.length ? `${parts.join(" · ")}.` : null;
};

const CrmReportPage = ({ kind, note }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [openCard, setOpenCard] = useState(null);
  // What the fields show, and what the report was last worked out for:
  // nothing applies until Apply, as on the batch report.
  const [draft, setDraft] = useState(NO_FILTERS);
  const [filters, setFilters] = useState(NO_FILTERS);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getCrmReportSummary(kind, filters)
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, filters]);

  const filterCount = FILTER_FIELDS.filter((f) => filters[f] !== NO_FILTERS[f]).length;
  const changed = FILTER_FIELDS.some((f) => draft[f] !== filters[f]);

  const line = highlightLine(kind, summary?.highlights);
  const cards = summary?.cards || [];

  return (
    <div>
      <FilterSheet className="br-filters" label="Filter the report">
        <div className="pg-filters-fields br-fields">
          <CrmFilterFields kind={kind} value={draft} onChange={setDraft} idPrefix="crm" />
        </div>

        <div className="br-filters-row">
          <div className="pg-filters-actions br-filters-acts">
            {filterCount > 0 && (
              <span className="br-filters-count">
                {filterCount} filter{filterCount === 1 ? "" : "s"}
              </span>
            )}
            <button
              type="button"
              className="pg-btn"
              onClick={() => {
                setDraft(NO_FILTERS);
                setFilters(NO_FILTERS);
              }}
              disabled={filterCount === 0 && !changed}
            >
              Reset
            </button>
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFilters(draft)}
              disabled={!changed}
            >
              Apply
            </button>
          </div>
        </div>
      </FilterSheet>

      {loading && (
        <div className="pg-state">
          <span className="pg-spinner" role="status" aria-hidden="true" />
          <p className="pg-state-text">Working out the report...</p>
        </div>
      )}

      {!loading && failed && (
        <div className="pg-state">
          <p className="pg-state-text">The report could not be loaded. Try again in a moment.</p>
        </div>
      )}

      {!loading && !failed && summary && (
        <>
          {/* Each figure opens the list it counted; a zero stays a plain
              figure rather than opening an empty list. */}
          <div className="br-stats">
            {cards.map((card) => (
              <StatTile
                key={card.key}
                num={card.count}
                label={card.label}
                variant={VARIANT[card.tone]}
                onClick={card.count > 0 ? () => setOpenCard(card) : undefined}
              />
            ))}
          </div>
          {line && <p className="pg-sub">{line}</p>}
          {note && <p className="pg-state-text">{note}</p>}

          {/* The figures lead; the charts follow. */}
          <CrmCharts kind={kind} charts={summary.charts} total={summary.total} />

        </>
      )}

      {openCard && (
        <CrmListModal kind={kind} card={openCard} filters={filters} onClose={() => setOpenCard(null)} />
      )}
    </div>
  );
};

export default CrmReportPage;
